import React, { useEffect, useRef, useState } from 'react';
import intl from '$intl';
import { Formik } from 'formik';
import { IonContent, IonFooter } from '@ionic/react';

import { input } from '$gbusiness/helpers';
import { FormSection } from '$gcomponents/reusables';
import { Col, Row, SPACE } from '$gstyles';
import { Flex, PageWrapper } from '$gstyles/wrapper';
import { Button, Counter } from '$gcomponents/primitives';
import { Modal } from '$gcomponents/reusables';
import { BATCH_FORM, BATCH_MEMO_FORM, BATCH_SCAN_FORM, BATCH_SERIALS_FORM } from './batchForm';
import FactoryModel from '$fbusiness/models/factory';
import ItemModel from '$fbusiness/models/item';
import { getNextIdF } from '$fbusiness/redux/factory/actions';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { itemActions } from '$fbusiness/redux/item';
import { itemText, toNumber } from '$gbusiness/helpers/util';
import { dateToString } from '$gbusiness/helpers/date';
import { batchActions } from '$fbusiness/redux/batch';
import ItemBatchModel, { initialBatch } from '$fbusiness/models/itemBatch';
import { parseISO } from 'date-fns/esm';
import { accessDisable, getAccess } from '$fbusiness/helpers/util';
import CurrentStateModel from '$fbusiness/models/currentState';
import { toastWarning } from '$gbusiness/redux/toaster/actions';

interface BatchModalProps {
  factory: FactoryModel;
  item?: ItemModel;
  batch?: ItemBatchModel;
  isItemPage?: boolean;
  show: boolean;
  currentState: CurrentStateModel;
  onRefresh?: Function;
  readOnly?: boolean;
  onClose: Function;
}

const BatchModal: React.FC<BatchModalProps> = ({
  factory,
  item,
  batch = initialBatch,
  show,
  currentState,
  readOnly = false,
  isItemPage,
  onRefresh,
  onClose,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state: any) => state.item.list);
  const formRef = useRef<any>();
  const [loop, setLoop] = useState<any>(null);
  const [scanMode, setScanMode] = useState(false);
  const [scannedItems, setScannedItems] = useState<any[]>([]);
  const ACCESS = getAccess(currentState);
  const isEdit: boolean = batch?.id > 0;

  const { vendors, settings } = factory;

  useEffect(() => {
    if (!show) {
      if (loop) setLoop(null);
      return;
    }
    setTimeout(checkReceivingNo, 600);
    setScanMode(false);
    setLoop(setInterval(checkReceivingNo, 10000));
    if (!items.length) {
      dispatch(itemActions.fetchItemList());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const onScanValue = (formik, value) => {
    const item = items.find((i) => i.barcode === value);
    if (!item) {
      dispatch(toastWarning({ text: intl('MESSAGE.BARCODE_NOT_FOUND', { barcode: value }) }));
      formik.setFieldValue('scanText', '');
      return;
    }
    const scannedItem = scannedItems.find((i) => i.id === item.id);
    if (!scannedItem) {
      const newItems = [...scannedItems, { ...item, quantity: 1 }];
      setScannedItems(newItems);
      updateTotalQty(newItems);
    } else {
      const newScannedItems = scannedItems.map((i) => {
        if (i.id === item.id) {
          return { ...i, quantity: i.quantity + 1 };
        }
        return i;
      });
      setScannedItems(newScannedItems);
      updateTotalQty(newScannedItems);
    }
    formik.setFieldValue('scanText', '');
  };

  const updateTotalQty = (newItems) => {
    const totalQty = newItems.reduce((acc, item) => acc + Number(item.quantity), 0);
    formRef.current.setFieldValue('quantity', totalQty);
  };

  const onChangeItemQty = (index, value) => {
    const newScannedItems = scannedItems.map((item, i) => {
      if (i === index) {
        return { ...item, quantity: Number(value) };
      }
      return item;
    });
    setScannedItems(newScannedItems);
    updateTotalQty(newScannedItems);
  };

  const onChangeVendor = (id) => {
    const vendor = vendors.find((v) => v.id === id);
    formRef.current.setFieldValue('vendor', vendor || null);
  };

  const batchForm = BATCH_FORM({
    onChangeMode: setScanMode,
    vendors,
    onChangeVendor,
    scanMode,
    isEdit,
    warehouses: factory.warehouses,
    items: items || [],
    settings,
    disabled: readOnly,
  });
  const batchScanForm = BATCH_SCAN_FORM({
    scanMode,
    onScanValue,
  });
  const batchMemoForm = BATCH_MEMO_FORM({ disabled: readOnly });
  const batchSerialForm = BATCH_SERIALS_FORM({
    isEdit,
    settings,
  });

  const checkReceivingNo = () => {
    if (batch?.id) return;
    if (formRef?.current && !formRef.current.values?.receiveNo) {
      getNextIdF('item_batches', 'receiveNo').then(async (id) => {
        if (formRef?.current) formRef.current.setFieldValue('receiveNo', id);
      });
    }
  };

  const onSubmit = (values) => {
    const { item, serials, cost, vendor, expirationDate, receiveDate, sellByDate, ...rest } = values;
    const param = {
      ...rest,
      vendorId: vendor?.id || null,
      itemId: item?.id || 0,
      cost: toNumber(cost),
      expirationDate: dateToString(expirationDate),
      receiveDate: dateToString(receiveDate),
      sellByDate: dateToString(sellByDate),
      serials: batch.id ? undefined : (serials || '').trim().split(/[\n,]/),
    };

    if (scanMode) {
      dispatch(batchActions.addBulkBatch({ ...param, itemId: undefined, items: scannedItems })).then(
        (success) => {
          if (success) {
            if (onRefresh) onRefresh();
            if (isItemPage && item?.id) dispatch(itemActions.fetchItem(item?.id));
            onClose();
          }
        },
      );
      return;
    }
    dispatch(batchActions.saveBatch(batch?.id, param)).then((success) => {
      if (success) {
        if (onRefresh) onRefresh();
        if (isItemPage && item?.id) dispatch(itemActions.fetchItem(item?.id));
        onClose();
      }
    });
  };

  const validateForm = (values) => {
    return input.validateError(batchForm, values);
  };

  const initialValues = {
    item,
    quantity: null,
    scanMode: false,
    ...(batch?.id
      ? {
          ...batch,
          ...(batch.receiveDate && { receiveDate: parseISO(batch.receiveDate) }),
          ...(batch.sellByDate && { sellByDate: parseISO(batch.sellByDate) }),
          ...(batch.expirationDate && { expirationDate: parseISO(batch.expirationDate) }),
          ...(batch.warehouse && { warehouseId: batch.warehouse.id }),
          serials: batch.serials.join('\n'),
        }
      : { receiveDate: new Date(), ...(item?.warehouseId && { warehouseId: item.warehouseId }) }),
  };

  const title = batch?.id ? itemText('EDIT', 'BATCH') : itemText('ADD_NEW', 'BATCH');
  const disableClass = accessDisable(
    batch?.id ? ACCESS.ACTION.RECEIVING.EDIT : ACCESS.ACTION.RECEIVING.CREATE,
  );

  return (
    <Modal width="600px" titleText={title} show={show} onClose={() => onClose()} useCustom>
      <Formik
        innerRef={formRef}
        enableReinitialize={false}
        initialValues={initialValues}
        validate={validateForm}
        onSubmit={(values) => {
          onSubmit(values);
        }}>
        {(formik) => (
          <>
            <IonContent>
              <PageWrapper>
                <FormSection FORM={batchForm} formik={formik} marginBottom="0" />
                {!scanMode ? (
                  <Row>
                    <Col gridSize={6} paddingRight={SPACE.MEDIUM}>
                      <FormSection FORM={batchMemoForm} formik={formik} marginBottom="0" />
                    </Col>
                    <Col gridSize={6} paddingLeft={SPACE.MEDIUM}>
                      <FormSection FORM={batchSerialForm} formik={formik} marginBottom="0" />
                    </Col>
                  </Row>
                ) : (
                  <Row>
                    <Col gridSize={6} paddingRight={SPACE.MEDIUM}>
                      <FormSection FORM={batchScanForm} formik={formik} marginBottom="0" />
                    </Col>
                    <Col gridSize={6} paddingLeft={SPACE.MEDIUM}>
                      {scannedItems.length > 0 && (
                        <div className="scanned-items">
                          {scannedItems.map((item, i) => (
                            <Flex className="scanned-item" key={i}>
                              <div className="item-name">{item.name}</div>
                              <Counter
                                size={14}
                                edit
                                onChange={(val) => onChangeItemQty(i, val)}
                                value={item.quantity}
                              />
                            </Flex>
                          ))}
                        </div>
                      )}
                    </Col>
                  </Row>
                )}
                {batch?.id > 0 && batch?.serials.length > 0 && (
                  <div className="serials">
                    <h4>Serials</h4>
                    {batch?.serials.map((s, i) => (
                      <div className="serial" key={i}>
                        {s.serial}
                      </div>
                    ))}
                  </div>
                )}
              </PageWrapper>
            </IonContent>
            {
              <IonFooter>
                <Flex padding={SPACE.MEDIUM} justifyContent="flex-end">
                  <Button
                    disabled={!(formik.isValid && formik.dirty)}
                    className={disableClass}
                    onClick={formik.handleSubmit}>
                    {intl('BUTTON.SUBMIT')}
                  </Button>
                </Flex>
              </IonFooter>
            }
          </>
        )}
      </Formik>
    </Modal>
  );
};

export default BatchModal;
